import type { ComponentType } from 'react';
import { BookOpen, Layers, Sparkles } from 'lucide-react';

export interface StepMetaItem {
  title: string;
  icon: ComponentType<{ className?: string }>;
}

interface StepHeaderProps {
  currentStep: number;
  steps: StepMetaItem[];
  courseName?: string | null;
  sectionName?: string | null;
  label?: string;
}

export const StepHeader = ({
  currentStep,
  steps,
  courseName,
  sectionName,
  label = 'Assignment wizard',
}: StepHeaderProps) => (
  <div className="sticky top-14 z-30 rounded-2xl border bg-gradient-to-r from-slate-50 via-white to-slate-100/90 p-4 shadow-sm backdrop-blur-sm dark:from-slate-900 dark:via-slate-950/90 dark:to-slate-900/90 md:top-0">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary shadow-sm">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold">Step {currentStep + 1} of {steps.length}</p>
        </div>
      </div>
      <div className="min-w-[220px] flex-1">
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
          {steps.map(({ title, icon: Icon }, index) => (
            <span
              key={title}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 transition-colors ${
                currentStep === index
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-transparent bg-muted/40'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{title}</span>
            </span>
          ))}
        </div>
        <div className="mt-2 hidden flex-wrap gap-2 text-xs text-muted-foreground sm:flex">
          {courseName ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/70 px-2 py-1">
              <BookOpen className="h-3.5 w-3.5 text-primary" />
              {courseName}
            </span>
          ) : null}
          {sectionName ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-muted bg-muted/40 px-2 py-1">
              <Layers className="h-3.5 w-3.5 text-muted-foreground" />
              {sectionName}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);
